import { useState, useRef, useEffect } from 'react'
import { X, Send, Bot, User, MessageCircle } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { t } from '../i18n'
import { quickReplies } from '../data/chatResponses'

const WHATSAPP_NUMBER = '201028137221'
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`

interface Message {
  id: number
  from: 'bot' | 'user'
  text: string
}

interface ChatBotProps {
  open: boolean
  onClose: () => void
}

export default function ChatBot({ open, onClose }: ChatBotProps) {
  const { lang } = useApp()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [showQuick, setShowQuick] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const nextId = useRef(1)

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ id: nextId.current++, from: 'bot', text: t(lang, 'chatWelcome') }])
    }
  }, [open, lang, messages.length])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const addMessage = (from: 'bot' | 'user', text: string) => {
    setMessages((prev) => [...prev, { id: nextId.current++, from, text }])
  }

  const handleQuickReply = (qr: typeof quickReplies[0]) => {
    const userLabel = lang === 'ar' ? qr.labelAr : qr.labelEn
    const botResponse = lang === 'ar' ? qr.responseAr : qr.responseEn
    addMessage('user', userLabel)
    setShowQuick(false)
    setTimeout(() => addMessage('bot', botResponse), 500)
  }

  const handleSend = () => {
    const trimmed = input.trim()
    if (!trimmed) return
    addMessage('user', trimmed)
    setInput('')
    setShowQuick(false)
    setTimeout(() => {
      const fallback =
        lang === 'ar'
          ? 'شكراً لتواصلك! سيتواصل معك فريقنا قريباً. يمكنك أيضاً التواصل معنا مباشرة عبر واتساب 👇'
          : 'Thank you! Our team will contact you shortly. You can also reach us directly via WhatsApp 👇'
      addMessage('bot', fallback)
    }, 600)
  }

  if (!open) return null

  return (
    <div className={`chatbot-overlay ${lang === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="chatbot-window">
        <div className="chatbot-header">
          <div className="chatbot-title">
            <Bot size={20} />
            <span>{t(lang, 'chatTitle')}</span>
          </div>
          <button className="chatbot-close" onClick={onClose} aria-label="close">
            <X size={20} />
          </button>
        </div>

        <div className="chatbot-body">
          {messages.map((msg) => (
            <div key={msg.id} className={`chat-msg ${msg.from}`}>
              <div className="chat-avatar">
                {msg.from === 'bot' ? <Bot size={16} /> : <User size={16} />}
              </div>
              <div className="chat-bubble">
                {msg.text.split('\n').map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < msg.text.split('\n').length - 1 && <br />}
                  </span>
                ))}
              </div>
            </div>
          ))}

          {showQuick && (
            <div className="quick-replies">
              <p className="quick-label">{t(lang, 'quickReplies')}</p>
              {quickReplies.map((qr, i) => (
                <button
                  key={i}
                  className="quick-reply-btn"
                  onClick={() => handleQuickReply(qr)}
                >
                  {lang === 'ar' ? qr.labelAr : qr.labelEn}
                </button>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* WhatsApp Support Button */}
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-whatsapp-btn"
        >
          <MessageCircle size={18} />
          {lang === 'ar' ? 'تواصل مع الدعم عبر واتساب' : 'Contact Support on WhatsApp'}
        </a>

        <div className="chatbot-footer">
          <input
            className="chat-input"
            placeholder={t(lang, 'chatPlaceholder')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
          />
          <button className="chat-send-btn" onClick={handleSend} aria-label="send">
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}